/**
 * Factor Indicator Components
 *
 * IndicatorBadge renders a Yes/No/N/A pill badge for a boolean or null indicator.
 * Column config arrays define the sub-columns for the two-row grouped header.
 */

import { LucideIcon } from "lucide-react";
import {
  HeartPulse,
  FileText,
  Pill,
  Bug,
  Syringe,
  CalendarClock,
  CircleAlert,
  CircleCheck,
  CircleMinus,
  // AlertTriangle — retained for Sig. Change re-enablement
  Activity,
  Footprints,
} from "lucide-react";
import { FallsIcon } from "../atoms/FallsIcon";
import { NewInsights, PrimaryRiskFactors } from "../../types/residents";

// ============================================================================
// Column config arrays (used by ResidentsTable header + ResidentRow cells)
// ============================================================================

export interface IndicatorColumnConfig {
  key: string;
  label: string;
  icon: LucideIcon;
}

export const NEW_INSIGHT_COLUMNS: IndicatorColumnConfig[] = [
  { key: "vital_changes", label: "Vital Changes", icon: HeartPulse },
  { key: "progress_notes", label: "Progress Notes", icon: FileText },
  { key: "high_risk_meds", label: "High-Risk Meds", icon: Pill },
  { key: "low_risk_meds", label: "Other Meds", icon: Pill },
  { key: "infections", label: "Infections", icon: Bug },
  { key: "falls", label: "Falls", icon: FallsIcon },
  { key: "adl_increase", label: "Self-care Declines", icon: Activity },
  { key: "locomotion_decline", label: "Mobility Declines", icon: Footprints },
  // { key: "significant_change", label: "Sig. Change", icon: AlertTriangle },
  // Hidden per stakeholder request — retained for re-enablement
];

export const ADMITTED_COLUMN: IndicatorColumnConfig = {
  key: "admitted_lt_30_days",
  label: "Admitted < 30 Days",
  icon: CalendarClock,
};

export const PRIMARY_RISK_COLUMNS: IndicatorColumnConfig[] = [
  { key: "indwelling_catheter", label: "Indwelling Catheter", icon: Syringe },
  { key: "previous_falls", label: "Falls", icon: FallsIcon },
  { key: "receiving_antipsychotic", label: "Receiving Antipsychotic", icon: Pill },
  { key: "long_term_infections", label: "Active Infections", icon: Bug },
];

// ============================================================================
// Backend-to-frontend column key mapping
// ============================================================================

/**
 * Maps backend VALID_RISK_FACTOR_IDS to frontend column keys.
 * Used to translate hidden_risk_factors from the API config.
 */
const BACKEND_TO_FRONTEND_KEY: Record<string, string> = {
  vital_changes: "vital_changes",
  progress_notes_concerns: "progress_notes",
  high_risk_medications: "high_risk_meds",
  low_risk_medications: "low_risk_meds",
  infections: "infections",
  previous_falls: "previous_falls",
  significant_change_detected: "significant_change",
  indwelling_catheter: "indwelling_catheter",
  receiving_antipsychotic: "receiving_antipsychotic",
  long_term_infections: "long_term_infections",
  admitted_less_30_days: "admitted_lt_30_days",
  adl_increase: "adl_increase",
  locomotion_decline: "locomotion_decline",
};

/**
 * Filter column arrays by removing columns whose backend IDs are in the
 * hidden list. Returns new arrays — does not mutate the originals.
 */
export function filterVisibleColumns(
  columns: IndicatorColumnConfig[],
  hiddenBackendIds: string[]
): IndicatorColumnConfig[] {
  if (!hiddenBackendIds || hiddenBackendIds.length === 0) return columns;
  const hiddenFrontendKeys = new Set(
    hiddenBackendIds.map((id) => BACKEND_TO_FRONTEND_KEY[id]).filter(Boolean)
  );
  return columns.filter((col) => !hiddenFrontendKeys.has(col.key));
}

// ============================================================================
// IndicatorBadge — renders a single Yes/No/N/A pill
// ============================================================================

interface IndicatorBadgeProps {
  value: boolean | null;
}

export function IndicatorBadge({ value }: IndicatorBadgeProps) {
  if (value === null) {
    return (
      <span
        className="inline-flex items-center justify-center gap-1 rounded-md px-3 py-1 text-xs font-semibold whitespace-nowrap"
        style={{
          backgroundColor: "#F3F4F6",
          color: "#6B7280",
          border: "1px solid #D1D5DB",
        }}
      >
        <CircleMinus className="h-3 w-3" />
        N/A
      </span>
    );
  }

  if (value) {
    return (
      <span
        className="inline-flex items-center justify-center gap-1 rounded-md px-3 py-1 text-xs font-bold whitespace-nowrap"
        style={{
          backgroundColor: "#FEE2E2",
          color: "#B02828",
          border: "1px solid #B02828",
        }}
      >
        <CircleAlert className="h-3 w-3" />
        Yes
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center justify-center gap-1 rounded-md px-3 py-1 text-xs font-semibold whitespace-nowrap"
      style={{
        backgroundColor: "#DCFCE7",
        color: "#15803D",
        border: "1px solid #15803D",
      }}
    >
      <CircleCheck className="h-3 w-3" />
      No
    </span>
  );
}

// ============================================================================
// Helper to read a boolean from the insights/risk objects by key
// ============================================================================

export function getInsightValue(
  insights: NewInsights,
  key: string
): boolean | null {
  const val = (insights as unknown as Record<string, boolean | null>)[key];
  return val === undefined ? false : val;
}

export function getRiskFactorValue(
  factors: PrimaryRiskFactors,
  key: string
): boolean | null {
  const val = (factors as unknown as Record<string, boolean | null>)[key];
  return val === undefined ? null : val;
}
